import { useEffect, useMemo, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import Topbar from "@/components/Topbar";
import BirthdayCampaignCard from "@/components/campaigns/BirthdayCampaignCard";
import VariableInserter from "@/components/VariableInserter";
import { useSector } from "@/lib/sectorContext";
import { toast } from "sonner";
import {
  MegaphoneSimple, Plus, Play, Trash, Eye, X, Calendar,
  UsersThree, ChatCircleDots, CalendarCheck, Pulse, PencilSimple,
  Paperclip, FilePdf, Image as ImageIcon, Microphone,
} from "@phosphor-icons/react";

const STATUS_BADGE = {
  draft: "bg-slate-100 text-slate-700 border-slate-200",
  scheduled: "bg-blue-50 text-blue-700 border-blue-200",
  running: "bg-amber-50 text-amber-700 border-amber-200",
  completed: "bg-emerald-50 text-emerald-700 border-emerald-200",
  canceled: "bg-slate-100 text-slate-500 border-slate-200",
  failed: "bg-rose-50 text-rose-700 border-rose-200",
  safety_blocked: "bg-amber-50 text-amber-800 border-amber-300",
};

// Rótulos amigáveis (em PT-BR) pra cada status — o badge antes mostrava
// "safety_blocked" literal e ninguém entendia que era bloqueio de segurança.
const STATUS_LABEL = {
  draft: "rascunho",
  scheduled: "agendada",
  running: "enviando",
  completed: "concluída",
  canceled: "cancelada",
  failed: "falhou",
  safety_blocked: "bloqueada",
};

const AUDIENCE_LABEL = {
  all_customers: "Todos os clientes",
  active_only: "Clientes ativos",
  inactive_only: "Clientes inativos",
  vip_only: "Clientes VIP",
  no_vehicle: "Sem veículo cadastrado",
  leads: "Leads (sem cadastro)",
  by_vehicle: "Por veículo",
  by_last_service: "Por último serviço",
  by_upcoming_service: "Por data de troca/revisão",
};

// v0.98 · Fev/2026 — Multi-setor. Alguns labels de audiência falam de
// "veículo" (só faz sentido em automotivo). Se o setor for Pet Shop,
// Salão etc., escondemos essas opções da UI e sobrescrevemos o label
// do que sobrar. Fora automotivo, `no_vehicle` some (não faz sentido
// pra clínica), `by_vehicle` idem, `by_last_service` vira "Por último
// atendimento" e `by_upcoming_service` vira "Por próxima data prevista".
function useAudienceLabels() {
  const { isAutomotive, labels } = useSector();
  const asset = (labels?.assetSingular?.() || "veículo").toLowerCase();
  const service = (labels?.serviceSingular?.() || "serviço").toLowerCase();
  const map = { ...AUDIENCE_LABEL };
  if (!isAutomotive) {
    delete map.no_vehicle;
    delete map.by_vehicle;
    map.by_last_service = `Por último ${service}`;
    map.by_upcoming_service = `Por próxima data prevista`;
  }
  return { map, isAutomotive, asset, service };
}

// v0.96.20 — Modo `by_upcoming_service` usa dados vindos do upload de
// planilha de clientes (Smart Data Adapter). v0.96.21 — também aceita
// campos "projetados pela IA" (calculados a partir de km atual + histórico
// + intervalo típico da marca/ano/combustível). O operador vê a
// diferença no label ("estimado pela IA").
const SERVICE_FIELD_LABEL = {
  next_oil_change_at: "Próxima troca de óleo (uploadeada)",
  next_service_at:    "Próxima revisão (uploadeada)",
  last_oil_change_at: "Última troca de óleo",
  last_service_at:    "Última revisão",
  projected_next_oil_change_at: "Próxima troca — estimada pela IA (km + histórico)",
  projected_next_service_at:    "Próxima revisão — estimada pela IA (km + histórico)",
};

// v0.98 · Fev/2026 — Labels de campo de serviço variam por setor.
// Automotivo tem "troca de óleo/revisão"; outros setores falam de
// "próximo atendimento/retorno" genérico.
function useServiceFieldLabels() {
  const { isAutomotive } = useSector();
  if (isAutomotive) return SERVICE_FIELD_LABEL;
  return {
    next_service_at:    "Próxima data prevista (uploadeada)",
    last_service_at:    "Último atendimento",
    projected_next_service_at: "Próxima data — estimada pela IA",
  };
}

// Pré-sets de janela pra o operador não precisar catar data. Custom
// segue disponível abaixo.
function _addDays(days) {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}
const _today = () => new Date().toISOString().slice(0, 10);

function fmtDateTime(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("pt-BR", {
    day: "2-digit", month: "2-digit", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

function StatusPill({ status }) {
  const cls = STATUS_BADGE[status] || "bg-slate-100 text-slate-600 border-slate-200";
  return (
    <span className={`inline-block text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border ${cls}`}>
      {STATUS_LABEL[status] || status}
    </span>
  );
}

function StatCard({ icon: Icon, label, value }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-5 hover:border-slate-300 transition-colors" data-testid={`campaign-stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-start justify-between">
        <div className="text-[11px] uppercase tracking-[0.16em] text-slate-500 font-medium">{label}</div>
        <Icon size={16} weight="regular" className="text-slate-400" />
      </div>
      <div className="font-heading text-[28px] font-semibold text-slate-900 tracking-tight leading-none mt-3 tabular-nums">{value}</div>
    </div>
  );
}

function AudienceForm({ value, onChange }) {
  const set = (patch) => onChange({ ...value, ...patch });
  const { map: audienceLabel, isAutomotive } = useAudienceLabels();
  const serviceFieldLabel = useServiceFieldLabels();
  return (
    <div className="space-y-3">
      <div>
        <label className="text-[11px] uppercase tracking-[0.16em] text-slate-500 font-medium">Público-alvo</label>
        <select
          value={value.mode}
          onChange={(e) => set({ mode: e.target.value })}
          data-testid="audience-mode-select"
          className="mt-1.5 w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:border-slate-900"
        >
          {Object.entries(audienceLabel).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>
      </div>

      {(value.mode === "active_only" || value.mode === "inactive_only") && (
        <div>
          <label className="text-[11px] uppercase tracking-[0.16em] text-slate-500 font-medium">
            {value.mode === "active_only" ? "Ativos nos últimos (dias)" : "Sem contato há mais de (dias)"}
          </label>
          <input
            type="number"
            value={value.days || (value.mode === "active_only" ? 30 : 60)}
            onChange={(e) => set({ days: parseInt(e.target.value, 10) })}
            data-testid="audience-days-input"
            className="mt-1.5 w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:border-slate-900"
          />
        </div>
      )}

      {value.mode === "by_vehicle" && isAutomotive && (
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-[11px] uppercase tracking-[0.16em] text-slate-500 font-medium">Marca</label>
            <input
              type="text"
              value={value.brand || ""}
              onChange={(e) => set({ brand: e.target.value })}
              placeholder="ex: BYD"
              data-testid="audience-brand-input"
              className="mt-1.5 w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:border-slate-900"
            />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-[0.16em] text-slate-500 font-medium">Combustível</label>
            <input
              type="text"
              value={value.fuel || ""}
              onChange={(e) => set({ fuel: e.target.value })}
              placeholder="diesel, flex…"
              data-testid="audience-fuel-input"
              className="mt-1.5 w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:border-slate-900"
            />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-[0.16em] text-slate-500 font-medium">Ano de</label>
            <input
              type="number"
              value={value.year_from || ""}
              onChange={(e) => set({ year_from: e.target.value ? parseInt(e.target.value, 10) : null })}
              data-testid="audience-year-from-input"
              className="mt-1.5 w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:border-slate-900"
            />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-[0.16em] text-slate-500 font-medium">até</label>
            <input
              type="number"
              value={value.year_to || ""}
              onChange={(e) => set({ year_to: e.target.value ? parseInt(e.target.value, 10) : null })}
              data-testid="audience-year-to-input"
              className="mt-1.5 w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:border-slate-900"
            />
          </div>
          {/* v0.96.21 — Filtro por quilometragem atual do veículo.
              Útil pra pegar "clientes com km entre 40k e 60k" (perto de
              revisão major, por exemplo). */}
          <div>
            <label className="text-[11px] uppercase tracking-[0.16em] text-slate-500 font-medium">KM de</label>
            <input
              type="number"
              value={value.km_from || ""}
              onChange={(e) => set({ km_from: e.target.value ? parseInt(e.target.value, 10) : null })}
              placeholder="ex: 40000"
              data-testid="audience-km-from-input"
              className="mt-1.5 w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:border-slate-900"
            />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-[0.16em] text-slate-500 font-medium">KM até</label>
            <input
              type="number"
              value={value.km_to || ""}
              onChange={(e) => set({ km_to: e.target.value ? parseInt(e.target.value, 10) : null })}
              placeholder="ex: 60000"
              data-testid="audience-km-to-input"
              className="mt-1.5 w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:border-slate-900"
            />
          </div>
        </div>
      )}

      {value.mode === "by_last_service" && (
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-[11px] uppercase tracking-[0.16em] text-slate-500 font-medium">Código do serviço</label>
            <input
              type="text"
              value={value.service_code || ""}
              onChange={(e) => set({ service_code: e.target.value })}
              placeholder="ex: oil_change"
              data-testid="audience-service-input"
              className="mt-1.5 w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:border-slate-900"
            />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-[0.16em] text-slate-500 font-medium">Últimos (dias)</label>
            <input
              type="number"
              value={value.days || ""}
              onChange={(e) => set({ days: e.target.value ? parseInt(e.target.value, 10) : null })}
              placeholder="opcional"
              className="mt-1.5 w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:border-slate-900"
            />
          </div>
        </div>
      )}

      {/* v0.96.20 — Novo modo: filtra pelas datas de manutenção que
          chegam via upload de planilha de clientes. Ver docstring do
          `_customer_ids_by_upcoming_service` no engine. */}
      {value.mode === "by_upcoming_service" && (
        <div className="space-y-3 border-l-2 border-slate-200 pl-3">
          <div>
            <label className="text-[11px] uppercase tracking-[0.16em] text-slate-500 font-medium">
              Campo alvo
            </label>
            <select
              value={value.service_field || "next_oil_change_at"}
              onChange={(e) => set({ service_field: e.target.value })}
              data-testid="audience-service-field-select"
              className="mt-1.5 w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:border-slate-900"
            >
              {Object.entries(serviceFieldLabel).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-[11px] uppercase tracking-[0.16em] text-slate-500 font-medium">
              Janela
            </label>
            <div className="mt-1.5 grid grid-cols-4 gap-1.5">
              {[
                { label: "7 dias",  from: () => _today(), to: () => _addDays(7) },
                { label: "15 dias", from: () => _today(), to: () => _addDays(15) },
                { label: "30 dias", from: () => _today(), to: () => _addDays(30) },
                { label: "60 dias", from: () => _today(), to: () => _addDays(60) },
              ].map((p) => (
                <button
                  key={p.label}
                  type="button"
                  onClick={() => set({ date_from: p.from(), date_to: p.to() })}
                  data-testid={`audience-preset-${p.label.replace(/\s+/g, "-")}`}
                  className="px-2 py-1.5 border border-slate-300 rounded-md text-[11px] font-medium hover:border-slate-900 hover:bg-slate-50 transition-colors"
                >
                  Próx. {p.label}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] uppercase tracking-[0.16em] text-slate-500 font-medium">
                De
              </label>
              <input
                type="date"
                value={value.date_from || ""}
                onChange={(e) => set({ date_from: e.target.value || null })}
                data-testid="audience-date-from-input"
                className="mt-1.5 w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:border-slate-900"
              />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-[0.16em] text-slate-500 font-medium">
                Até
              </label>
              <input
                type="date"
                value={value.date_to || ""}
                onChange={(e) => set({ date_to: e.target.value || null })}
                data-testid="audience-date-to-input"
                className="mt-1.5 w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:border-slate-900"
              />
            </div>
          </div>

          <div className="text-[11px] text-slate-500 leading-relaxed">
            Filtra clientes cujo veículo tem essa data dentro do intervalo.
            Os dados vêm da planilha de clientes que você subiu (colunas
            &quot;Próxima troca de óleo&quot;, &quot;Próxima revisão&quot; etc.). Clientes sem
            essa data preenchida não entram.
          </div>
        </div>
      )}
    </div>
  );
}

function CampaignModal({ open, initial, onClose, onSaved }) {
  const { isAutomotive } = useSector();
  const [form, setForm] = useState(() => initial || {
    name: "",
    message_template: "Oi {nome_cliente}! ",
    media_url: "",
    audience: { mode: "all_customers" },
    scheduled_at: "",
  });
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadingMedia, setUploadingMedia] = useState(false);
  const messageRef = useRef(null);

  const onUploadMedia = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingMedia(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/campaigns/upload_media", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      // Promote to absolute URL so WhatsApp can fetch it
      const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
      const absolute = base + data.url;
      setForm((f) => ({ ...f, media_url: absolute }));
      toast.success(`Anexado: ${data.filename}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha no upload");
    } finally {
      setUploadingMedia(false);
      e.target.value = "";
    }
  };

  useEffect(() => {
    if (initial) {
      const iso = initial.scheduled_at ? new Date(initial.scheduled_at).toISOString().slice(0, 16) : "";
      setForm({
        name: initial.name || "",
        message_template: initial.message_template || "",
        media_url: initial.media_url || "",
        audience: initial.audience || { mode: "all_customers" },
        scheduled_at: iso,
      });
    } else {
      setForm({
        name: "",
        message_template: "Oi {nome_cliente}! ",
        media_url: "",
        audience: { mode: "all_customers" },
        scheduled_at: "",
      });
    }
    setPreview(null);
  }, [initial, open]);

  if (!open) return null;

  const previewAudience = async () => {
    setLoadingPreview(true);
    try {
      const { data } = await api.post("/campaigns/preview", form.audience);
      setPreview(data);
    } catch (e) {
      toast.error("Falha ao calcular público");
    } finally {
      setLoadingPreview(false);
    }
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Dê um nome à campanha");
    if (!form.message_template.trim()) return toast.error("Escreva a mensagem");
    if (!form.scheduled_at) return toast.error("Defina data e hora do disparo");
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        message_template: form.message_template,
        media_url: form.media_url || null,
        delivery_kind: form.delivery_kind || "text",
        audience: form.audience,
        scheduled_at: new Date(form.scheduled_at).toISOString(),
      };
      if (initial?.id) {
        await api.put(`/campaigns/${initial.id}`, payload);
        toast.success("Campanha atualizada");
      } else {
        await api.post("/campaigns", payload);
        toast.success("Campanha agendada");
      }
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao salvar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/50 z-50 flex items-center justify-center p-4" data-testid="campaign-modal">
      <div className="bg-white rounded-lg w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <header className="px-6 py-4 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white">
          <h3 className="font-heading text-lg font-semibold text-slate-900 tracking-tight">
            {initial ? "Editar campanha" : "Nova campanha"}
          </h3>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-900" data-testid="campaign-modal-close">
            <X size={18} weight="regular" />
          </button>
        </header>

        <div className="p-6 space-y-5">
          <div>
            <label className="text-[11px] uppercase tracking-[0.16em] text-slate-500 font-medium">Nome interno</label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder={isAutomotive ? "Ex: Lançamento pneus 2026" : "Ex: Campanha de reativação"}
              data-testid="campaign-name-input"
              className="mt-1.5 w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:border-slate-900"
            />
          </div>

          <div>
            <label className="text-[11px] uppercase tracking-[0.16em] text-slate-500 font-medium">Mensagem</label>
            <textarea
              ref={messageRef}
              value={form.message_template}
              onChange={(e) => setForm({ ...form, message_template: e.target.value })}
              rows={4}
              data-testid="campaign-message-input"
              className="mt-1.5 w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:border-slate-900 resize-none"
              placeholder={isAutomotive
                ? "Use {nome_cliente}, {veiculo}, {marca} para personalizar."
                : "Use {nome_cliente} para personalizar."}
            />
            <div className="mt-1.5">
              <VariableInserter
                textareaRef={messageRef}
                value={form.message_template}
                onChange={(v) => setForm({ ...form, message_template: v })}
                variables={isAutomotive
                  ? [
                      { token: "{nome_cliente}", label: "Primeiro nome" },
                      { token: "{veiculo}", label: "Veículo do cliente" },
                      { token: "{marca}", label: "Marca do veículo" },
                    ]
                  : [
                      { token: "{nome_cliente}", label: "Primeiro nome" },
                    ]}
              />
            </div>
          </div>

          {/* Modo de entrega — texto ou áudio (Concierge) */}
          <div data-testid="campaign-delivery-kind">
            <label className="text-[11px] uppercase tracking-[0.16em] text-slate-500 font-medium">Como enviar</label>
            <div className="mt-1.5 grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setForm({ ...form, delivery_kind: "text" })}
                data-testid="campaign-delivery-text"
                className={`text-left p-3 rounded-xl border transition-all ${
                  form.delivery_kind !== "audio"
                    ? "border-slate-900 bg-slate-900 text-white shadow-sm shadow-slate-900/15"
                    : "border-slate-200 bg-white hover:border-slate-400"
                }`}
              >
                <div className="flex items-center gap-2 mb-0.5">
                  <ChatCircleDots size={14} weight="fill" />
                  <span className="font-semibold text-[13px]">Texto</span>
                </div>
                <p className={`text-[11.5px] leading-snug ${form.delivery_kind !== "audio" ? "text-slate-300" : "text-slate-500"}`}>
                  Mensagem normal, alcance máximo. 1 crédito por envio.
                </p>
              </button>
              <button
                type="button"
                onClick={() => setForm({ ...form, delivery_kind: "audio" })}
                data-testid="campaign-delivery-audio"
                className={`text-left p-3 rounded-xl border transition-all ${
                  form.delivery_kind === "audio"
                    ? "border-slate-900 bg-slate-900 text-white shadow-sm shadow-slate-900/15"
                    : "border-slate-200 bg-white hover:border-slate-400"
                }`}
              >
                <div className="flex items-center gap-2 mb-0.5">
                  <Microphone size={14} weight="fill" />
                  <span className="font-semibold text-[13px]">Áudio</span>
                  <span className={`text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded ${form.delivery_kind === "audio" ? "bg-white/20" : "bg-slate-200 text-slate-700"}`}>concierge</span>
                </div>
                <p className={`text-[11.5px] leading-snug ${form.delivery_kind === "audio" ? "text-slate-300" : "text-slate-500"}`}>
                  Voz humanizada (ElevenLabs). Maior impacto. 15 créditos por envio. Cai pra texto se falhar.
                </p>
              </button>
            </div>
          </div>

          <div>
            <label className="text-[11px] uppercase tracking-[0.16em] text-slate-500 font-medium">Mídia (opcional)</label>
            <p className="text-[11px] text-slate-500 mt-0.5">Imagem, vídeo ou PDF para anexar — máx 25 MB.</p>

            {form.media_url ? (
              <div className="mt-2 flex items-center justify-between gap-3 px-3 py-2 border border-slate-200 rounded-md bg-slate-50" data-testid="campaign-media-attachment">
                <div className="flex items-center gap-2 min-w-0">
                  {/\.(jpg|jpeg|png|webp|gif)$/i.test(form.media_url) ? (
                    <img src={form.media_url} alt="" className="w-10 h-10 rounded object-cover shrink-0" />
                  ) : /\.pdf$/i.test(form.media_url) ? (
                    <FilePdf size={20} weight="regular" className="text-rose-600 shrink-0" />
                  ) : (
                    <ImageIcon size={20} weight="regular" className="text-slate-500 shrink-0" />
                  )}
                  <a
                    href={form.media_url}
                    target="_blank"
                    rel="noreferrer"
                    className="text-sm text-slate-800 hover:text-slate-900 truncate"
                  >
                    {form.media_url.split("/").pop()?.split("?")[0] || "Mídia anexada"}
                  </a>
                </div>
                <button
                  type="button"
                  onClick={() => setForm({ ...form, media_url: "" })}
                  data-testid="campaign-media-remove"
                  className="p-1 text-slate-500 hover:text-rose-600 shrink-0"
                >
                  <X size={14} />
                </button>
              </div>
            ) : (
              <div className="mt-2 grid grid-cols-1 sm:grid-cols-[1fr_auto_1fr] gap-2 items-stretch">
                <label className="flex items-center justify-center gap-2 px-3 py-3 border-2 border-dashed border-slate-300 rounded-md text-sm text-slate-600 hover:border-slate-900 hover:text-slate-900 cursor-pointer transition-colors" data-testid="campaign-media-upload">
                  <Paperclip size={14} />
                  {uploadingMedia ? "Enviando…" : "Fazer upload"}
                  <input
                    type="file"
                    accept="image/*,video/mp4,video/quicktime,application/pdf"
                    onChange={onUploadMedia}
                    disabled={uploadingMedia}
                    className="hidden"
                  />
                </label>
                <div className="flex items-center justify-center text-[11px] text-slate-400 uppercase tracking-wider">ou</div>
                <input
                  type="url"
                  value=""
                  onChange={(e) => setForm({ ...form, media_url: e.target.value })}
                  placeholder="Colar URL externa"
                  data-testid="campaign-media-url"
                  className="px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:border-slate-900"
                />
              </div>
            )}
          </div>

          <div>
            <label className="text-[11px] uppercase tracking-[0.16em] text-slate-500 font-medium">Disparar em</label>
            <input
              type="datetime-local"
              value={form.scheduled_at}
              onChange={(e) => setForm({ ...form, scheduled_at: e.target.value })}
              data-testid="campaign-scheduled-input"
              className="mt-1.5 w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:border-slate-900"
            />
          </div>

          <div className="pt-4 border-t border-slate-100">
            <AudienceForm value={form.audience} onChange={(a) => { setForm({ ...form, audience: a }); setPreview(null); }} />
            <div className="mt-3 flex items-center gap-3">
              <button
                onClick={previewAudience}
                disabled={loadingPreview}
                data-testid="campaign-preview-btn"
                className="inline-flex items-center gap-1.5 text-[12px] text-slate-700 hover:text-slate-900 border border-slate-300 hover:border-slate-900 rounded-md px-3 py-1.5 transition-colors"
              >
                <Eye size={12} /> {loadingPreview ? "Calculando…" : "Calcular público"}
              </button>
              {preview && (
                <span className="text-sm text-slate-700" data-testid="campaign-preview-result">
                  Essa campanha vai para <strong className="tabular-nums">{preview.audience_size}</strong> contato{preview.audience_size === 1 ? "" : "s"}.
                  {preview.sample?.length > 0 && (
                    <span className="text-slate-500 text-xs ml-2">
                      Ex: {preview.sample.slice(0, 3).map((s) => s.name).join(", ")}…
                    </span>
                  )}
                </span>
              )}
            </div>
          </div>
        </div>

        <footer className="px-6 py-4 border-t border-slate-200 flex items-center justify-end gap-3 sticky bottom-0 bg-white">
          <button onClick={onClose} className="text-[13px] text-slate-500 hover:text-slate-900 transition-colors">
            Cancelar
          </button>
          <button
            onClick={save}
            disabled={saving}
            data-testid="campaign-save-btn"
            className="inline-flex items-center gap-2 bg-slate-900 text-white px-4 py-2 rounded-md text-[13px] font-medium hover:bg-slate-700 transition-colors disabled:opacity-60"
          >
            <Calendar size={14} /> {saving ? "Salvando…" : "Agendar campanha"}
          </button>
        </footer>
      </div>
    </div>
  );
}

export default function Campaigns() {
  const [campaigns, setCampaigns] = useState([]);
  const [stats, setStats] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const { map: audienceLabel } = useAudienceLabels();

  const load = async () => {
    const [c, s] = await Promise.all([
      api.get("/campaigns"),
      api.get("/campaigns/stats/summary"),
    ]);
    setCampaigns(c.data);
    setStats(s.data);
  };

  useEffect(() => { load(); }, []);

  const onCreate = () => { setEditing(null); setModalOpen(true); };
  const onEdit = (c) => { setEditing(c); setModalOpen(true); };

  const onCancel = async (c) => {
    if (!confirm(`Cancelar a campanha "${c.name}"?`)) return;
    try {
      await api.post(`/campaigns/${c.id}/cancel`);
      toast.success("Campanha cancelada");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao cancelar");
    }
  };

  const onDelete = async (c) => {
    if (!confirm(`Excluir definitivamente a campanha "${c.name}"?`)) return;
    try {
      await api.delete(`/campaigns/${c.id}`);
      toast.success("Campanha excluída");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao excluir");
    }
  };

  const onRunNow = async (c) => {
    if (!confirm(`Disparar a campanha "${c.name}" agora? Vai enviar para ${c.audience_size} contatos.`)) return;
    try {
      const { data } = await api.post(`/campaigns/${c.id}/run`);
      if (data.skipped) {
        toast.info(`Pulado: ${data.error}. Ative o modo Produção em Recepção para disparar.`);
      } else if (data.error) {
        toast.error(data.error);
      } else {
        toast.success(`Disparada: ${data.sent} enviada(s), ${data.failed} falha(s).`);
      }
      load();
    } catch (e) {
      toast.error("Falha ao disparar");
    }
  };

  // Liberar campanha em `safety_blocked` — exige modo `production_lead_capture`
  // ativo. Confirmação explícita + warning porque vai DISPARAR pra todos os
  // contatos da audiência. Foi exatamente o ponto onde a Omega levou um disparo
  // surpresa por trocar o modo sem perceber que tinha campanha na fila.
  const onUnblock = async (c) => {
    const msg = `⚠️ ATENÇÃO: Liberar e DISPARAR AGORA a campanha "${c.name}"?\n\n`
      + `Isso vai enviar a mensagem para ${c.audience_size} contato(s) IMEDIATAMENTE.\n\n`
      + `Tem certeza?`;
    if (!confirm(msg)) return;
    try {
      const { data } = await api.post(`/campaigns/${c.id}/unblock`);
      if (data.error) {
        toast.error(data.error);
      } else {
        toast.success(`Liberada e disparada: ${data.sent} enviada(s), ${data.failed} falha(s).`);
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao liberar");
    }
  };

  const upcoming = useMemo(() => campaigns.filter((c) => c.status === "scheduled").length, [campaigns]);

  return (
    <>
      <Topbar
        title="Campanhas"
        subtitle="Agende mensagens em massa, anuncie lançamentos e ative grupos específicos da sua base."
        actions={
          <div className="flex items-center gap-2">
            <Link
              to="/app/campanhas/recorrentes"
              data-testid="link-recurring-campaigns"
              className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-slate-300 hover:border-slate-900 hover:bg-slate-50 transition-colors text-[12px] font-medium text-slate-700"
              title="Automação de disparos recorrentes (aniversário, revisão, lembretes periódicos)"
            >
              Recorrentes
            </Link>
            <button
              onClick={onCreate}
              data-testid="campaign-new-btn"
              className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-slate-900 text-white hover:bg-slate-800 transition-colors text-[12px] font-medium shadow-sm"
            >
              <Plus size={14} weight="regular" /> Nova campanha
            </button>
          </div>
        }
      />
      <div className="p-6 md:p-8 space-y-6">
        {/* v0.97-e — Card destaque: Aniversário do cliente */}
        <BirthdayCampaignCard />

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard icon={Calendar} label="Agendadas" value={stats?.scheduled ?? "—"} />
          <StatCard icon={Pulse} label="Em execução" value={stats?.running ?? "—"} />
          <StatCard icon={ChatCircleDots} label="Mensagens enviadas" value={stats?.total_sent ?? "—"} />
          <StatCard icon={CalendarCheck} label="Agendamentos gerados" value={stats?.total_appointments ?? "—"} />
        </div>

        {stats?.next_campaign && (
          <div className="bg-slate-900 text-white rounded-lg p-5 flex items-center justify-between" data-testid="campaign-next-banner">
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-slate-400">Próxima campanha</div>
              <h3 className="font-heading text-lg font-semibold tracking-tight mt-1">{stats.next_campaign.name}</h3>
              <p className="text-sm text-slate-300 mt-0.5">
                Dispara em <span className="tabular-nums">{fmtDateTime(stats.next_campaign.scheduled_at)}</span> · {stats.next_campaign.audience_size} contato{stats.next_campaign.audience_size === 1 ? "" : "s"}
              </p>
            </div>
            <MegaphoneSimple size={28} weight="regular" className="text-slate-400" />
          </div>
        )}

        <section className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <header className="px-5 py-4 border-b border-slate-200">
            <h3 className="font-heading font-semibold tracking-tight text-slate-900">
              Histórico ({campaigns.length}) · {upcoming} agendada{upcoming === 1 ? "" : "s"}
            </h3>
          </header>
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left">
              <tr>
                <th className="px-4 py-2 text-[11px] uppercase tracking-wider text-slate-500 font-medium">Campanha</th>
                <th className="px-4 py-2 text-[11px] uppercase tracking-wider text-slate-500 font-medium">Disparo</th>
                <th className="px-4 py-2 text-[11px] uppercase tracking-wider text-slate-500 font-medium">Público</th>
                <th className="px-4 py-2 text-[11px] uppercase tracking-wider text-slate-500 font-medium text-right">Tamanho</th>
                <th className="px-4 py-2 text-[11px] uppercase tracking-wider text-slate-500 font-medium text-right">Enviadas</th>
                <th className="px-4 py-2 text-[11px] uppercase tracking-wider text-slate-500 font-medium text-right">Respostas</th>
                <th className="px-4 py-2 text-[11px] uppercase tracking-wider text-slate-500 font-medium">Status</th>
                <th className="px-4 py-2 text-right"></th>
              </tr>
            </thead>
            <tbody>
              {campaigns.length === 0 && (
                <tr><td colSpan="8" className="p-8 text-center text-slate-500">
                  Nenhuma campanha ainda. Clique em <strong>Nova campanha</strong> para agendar a primeira.
                </td></tr>
              )}
              {campaigns.map((c) => (
                <tr key={c.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`campaign-row-${c.id}`}>
                  <td className="px-4 py-3">
                    <div className="font-medium text-slate-900">{c.name}</div>
                    <div className="text-xs text-slate-500 line-clamp-1">{c.message_template}</div>
                  </td>
                  <td className="px-4 py-3 text-slate-700 tabular-nums">{fmtDateTime(c.scheduled_at)}</td>
                  <td className="px-4 py-3 text-slate-600">{audienceLabel[c.audience?.mode] || AUDIENCE_LABEL[c.audience?.mode] || c.audience?.mode}</td>
                  <td className="px-4 py-3 text-right text-slate-700 tabular-nums">{c.audience_size}</td>
                  <td className="px-4 py-3 text-right text-slate-700 tabular-nums">{c.sent}</td>
                  <td className="px-4 py-3 text-right text-slate-700 tabular-nums">{c.replies}</td>
                  <td className="px-4 py-3"><StatusPill status={c.status} /></td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    {c.status === "safety_blocked" && (
                      <button
                        onClick={() => onUnblock(c)}
                        data-testid={`campaign-unblock-${c.id}`}
                        title="Liberar e disparar"
                        className="inline-flex items-center gap-1 text-xs text-amber-700 hover:text-white hover:bg-amber-600 px-2 py-1 rounded-full border border-amber-300 mx-1 font-medium"
                      >
                        <Play size={12} weight="fill" /> Liberar
                      </button>
                    )}
                    {(c.status === "scheduled" || c.status === "failed") && (
                      <>
                        <button onClick={() => onEdit(c)} data-testid={`campaign-edit-${c.id}`} title="Editar" className="text-slate-500 hover:text-slate-900 mx-1">
                          <PencilSimple size={14} />
                        </button>
                        <button onClick={() => onRunNow(c)} data-testid={`campaign-run-${c.id}`} title="Disparar agora" className="text-slate-500 hover:text-emerald-600 mx-1">
                          <Play size={14} />
                        </button>
                        <button onClick={() => onCancel(c)} data-testid={`campaign-cancel-${c.id}`} title="Cancelar" className="text-slate-500 hover:text-rose-600 mx-1">
                          <X size={14} />
                        </button>
                      </>
                    )}
                    <button onClick={() => onDelete(c)} data-testid={`campaign-delete-${c.id}`} title="Excluir" className="text-slate-400 hover:text-rose-600 mx-1">
                      <Trash size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      </div>

      <CampaignModal
        open={modalOpen}
        initial={editing}
        onClose={() => setModalOpen(false)}
        onSaved={load}
      />
    </>
  );
}
